import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AuthLayout from '../../components/layout/AuthLayout';
import { getAuth, type MultiFactorResolver } from 'firebase/auth';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Shield, Clock } from 'lucide-react';
import { Spinner } from '../../components/common/Spinner';
import {
  classifyAuthError,
  clearAppCheckThrottleState,
  type AppCheckErrorContext,
} from '../../lib/appCheckRecovery';
import AppCheckRecoveryBanner from '../../components/auth/AppCheckRecoveryBanner';
import MfaChallengeCard from '../../components/auth/MfaChallengeCard';
import { isMfaRequiredError, buildMfaResolver } from '../../lib/nativeMfa';
import {
  isMfaEnrolmentRequiredSignInError,
  MFA_REQUIRED_SIGNIN_FULL_MESSAGE,
} from '../../lib/mfaErrors';

type ErrorCtx =
  | { kind: 'generic'; message: string }
  | { kind: 'appcheck'; ctx: AppCheckErrorContext };

// Feature gate for the App Check recovery UX. Default OFF in prod; opt-in via
// `?recoveryUx=on` query param or `localStorage['valutten:recoveryUx'] = 'on'`
// so we can soak the banner in stage before a wider flip. Remote-Config-backed
// gate deferred to a follow-up PR.
const APP_CHECK_RECOVERY_UX: boolean = (() => {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('recoveryUx') === 'on') return true;
    if (window.localStorage.getItem('valutten:recoveryUx') === 'on') return true;
  } catch {
    // localStorage may throw in private-browsing or with cookies disabled
  }
  return false;
})();

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errorCtx, setErrorCtx] = useState<ErrorCtx | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [googleSignInActive, setGoogleSignInActive] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>('');
  // When set, sign-in threw auth/multi-factor-auth-required and we render the
  // native TOTP challenge inline (the resolver is a live object and cannot be
  // routed through history state).
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const { login, signInWithGoogle, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = new URLSearchParams(location.search).get('expired') === 'true';
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Capture the original location the user was trying to reach before
  // ProtectedRoute redirected them to /login. Preserved across MFA via
  // navigate state. Falls back to '/' for direct /login visits.
  // Critical for the founder view-as flow: when the admin Console launches a
  // new tab at /?viewAs=<companyId> and the founder isn't authenticated, the
  // login redirect MUST preserve the ?viewAs= query param or AuthContext
  // won't see it after auth completes. See:
  //   docs/handovers/2026-05-04-FOUNDER-VERIFICATION-CONSOLE-AND-E2E-HANDOVER.md
  //   apps/client/src/context/AuthContext.tsx:380-405 (viewAs param read)
  //   apps/admin/src/pages/SubscriberVerificationConsolePage.tsx:386 (launcher URL)
  const fromLocation = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  const redirectTarget = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
    : '/';

  // Focus email input on mount for better UX
  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTarget, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTarget]);

  // Cleanup password hide timeout on unmount
  useEffect(() => {
    return () => {
      if (passwordHideTimeoutRef.current) {
        clearTimeout(passwordHideTimeoutRef.current);
      }
    };
  }, []);

  // Email validation helper
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email blur for validation
  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // Translate Firebase errors to user-friendly messages
  const getErrorMessage = (err: unknown): string => {
    const errObj = (err && typeof err === 'object') ? (err as { code?: unknown; message?: unknown }) : null;
    const errorCode = errObj && typeof errObj.code === 'string' ? errObj.code : '';
    const errorMessage = errObj && typeof errObj.message === 'string' ? errObj.message : '';

    // Don't show error for popup closed by user - this is a normal user action
    if (errorCode === 'auth/popup-closed-by-user') {
      return '';
    }

    // The MFA blocking function refused this sign-in. It throws
    // HttpsError('permission-denied', MSG_MFA_REQUIRED) from beforeUserSignedIn,
    // which the web SDK surfaces as `auth/internal-error` with the server text
    // inside `message`. Without this branch the `default:` arm below printed
    // that raw platform string, which reads as a Valutten outage rather than a
    // policy decision and tells the user nothing they can act on. The literal is
    // pinned to the server constant by mfaErrors.wireConstants.test.ts.
    if (isMfaEnrolmentRequiredSignInError(err)) {
      return MFA_REQUIRED_SIGNIN_FULL_MESSAGE;
    }

    switch (errorCode) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Email or password is incorrect';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support';
      case 'auth/invalid-email':
        return 'Please enter a valid email address';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      case 'auth/cancelled-popup-request':
        return ''; // User opened another popup, don't show error
      default:
        return errorMessage || 'An error occurred during login';
    }
  };

  // Handle password visibility toggle with auto-hide
  const togglePasswordVisibility = () => {
    setShowPassword(prev => {
      const newState = !prev;

      // If showing password, set timeout to hide it after 4 seconds
      if (newState) {
        if (passwordHideTimeoutRef.current) {
          clearTimeout(passwordHideTimeoutRef.current);
        }

        passwordHideTimeoutRef.current = setTimeout(() => {
          setShowPassword(false);
        }, 4000); // 4 seconds timeout
      } else {
        // If hiding password manually, clear the timeout
        if (passwordHideTimeoutRef.current) {
          clearTimeout(passwordHideTimeoutRef.current);
        }
      }

      return newState;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorCtx(null);
    setEmailError('');

    // Validate email before submitting
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await login(email, password);

      // MIGRATION-SAFE: users enrolled under the legacy bespoke MFA carry a
      // `mfaEnabled` claim + a speakeasy secret but NO native GCIP factor, so
      // login() resolves without throwing. Keep challenging them via the bespoke
      // /verify-2fa flow until the re-enrolment wave migrates them (plan Phase
      // 5-7; the claim is retired in Phase 9). Native-enrolled users never reach
      // this line — login() throws auth/multi-factor-auth-required, handled below.
      const idTokenResult = await getAuth().currentUser?.getIdTokenResult();
      if (idTokenResult?.claims?.mfaEnabled === true) {
        navigate('/verify-2fa', { state: { from: fromLocation } });
        return;
      }

      // AuthContext validates the profile (signing out if invalid).

    } catch (err: unknown) {
      // Native GCIP second-factor challenge required — capture the live resolver
      // and render the native challenge inline (it cannot cross router state).
      if (isMfaRequiredError(err)) {
        setMfaResolver(buildMfaResolver(getAuth(), err));
        return;
      }
      const ctx = classifyAuthError(err);
      if (ctx.isAppCheckFailure && APP_CHECK_RECOVERY_UX) {
        setErrorCtx({ kind: 'appcheck', ctx });
      } else {
        const msg = getErrorMessage(err);
        if (msg) setErrorCtx({ kind: 'generic', message: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorCtx(null);
    setEmailError('');
    setGoogleSignInActive(true);

    try {
      await signInWithGoogle();

      // MIGRATION-SAFE legacy bespoke MFA check (see handleSubmit). Google
      // provider users enrolled under the old system also carry mfaEnabled.
      const idTokenResult = await getAuth().currentUser?.getIdTokenResult();
      if (idTokenResult?.claims?.mfaEnabled === true) {
        navigate('/verify-2fa', { state: { from: fromLocation } });
        return;
      }

    } catch (err: unknown) {
      const errObj = (err && typeof err === 'object') ? (err as { code?: unknown }) : null;
      const errorCode = errObj && typeof errObj.code === 'string' ? errObj.code : '';

      // Don't show error for user-cancelled actions
      if (errorCode === 'auth/popup-closed-by-user' || errorCode === 'auth/cancelled-popup-request') {
        setGoogleSignInActive(false);
        return; // User just closed popup - not an error
      }

      // Native GCIP second-factor challenge required (also via Google sign-in) —
      // render the native challenge inline.
      if (isMfaRequiredError(err)) {
        setMfaResolver(buildMfaResolver(getAuth(), err));
        return;
      }

      const ctx = classifyAuthError(err);
      if (ctx.isAppCheckFailure && APP_CHECK_RECOVERY_UX) {
        setErrorCtx({ kind: 'appcheck', ctx });
      } else {
        const errorMessage = getErrorMessage(err);
        if (errorMessage) {
          setErrorCtx({ kind: 'generic', message: errorMessage });
        }
      }
    } finally {
      setGoogleSignInActive(false);
    }
  };
  
  // Native GCIP second-factor challenge — rendered inline once sign-in raised
  // auth/multi-factor-auth-required, so the live resolver stays in scope. On
  // success the user is fully signed in and we return them to their original
  // destination (preserving ?viewAs= for the founder view-as flow).
  if (mfaResolver) {
    return (
      <AuthLayout>
        <MfaChallengeCard
          resolver={mfaResolver}
          onSuccess={() => navigate(redirectTarget, { replace: true })}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
        {/* Header with gradient accent */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
            <Lock size={32} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Login</h2>
          <p className="text-gray-400 text-sm">Welcome back to VALUTTEN</p>
        </div>

        {/* Session Expired Banner */}
        {sessionExpired && !errorCtx && (
          <div className="bg-amber-900/40 border border-amber-500/50 text-amber-200 p-4 rounded-lg mb-6 flex items-start">
            <Clock size={20} className="mr-3 flex-shrink-0 mt-0.5 text-amber-400" />
            <span className="text-sm">Your session has expired due to inactivity. Please sign in again.</span>
          </div>
        )}

        {/* App Check Recovery Banner (feature-gated) */}
        {errorCtx?.kind === 'appcheck' && (
          <AppCheckRecoveryBanner
            ctx={errorCtx.ctx}
            onReset={async () => {
              await clearAppCheckThrottleState();
              window.location.assign('/login');
            }}
          />
        )}

        {/* Generic Error Alert */}
        {errorCtx?.kind === 'generic' && (
          <div
            className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-start"
            role="alert"
            aria-live="assertive"
          >
            <AlertCircle className="mr-3 flex-shrink-0 mt-0.5" size={20} />
            <span className="text-sm">{errorCtx.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block mb-2 text-sm font-medium text-gray-300"
            >
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={18} className="text-gray-400" />
              </div>
              <input
                ref={emailInputRef}
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                onBlur={handleEmailBlur}
                autoComplete="email"
                aria-label="Email address"
                aria-describedby={emailError ? "email-error" : undefined}
                aria-invalid={emailError ? "true" : "false"}
                className={`w-full bg-gray-700/50 border ${
                  emailError ? 'border-red-500' : 'border-gray-600'
                } text-white text-sm rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent pl-10 pr-3 py-3 transition-all duration-200`}
                required
              />
            </div>
            {emailError && (
              <p id="email-error" className="mt-1 text-xs text-red-400 flex items-center">
                <AlertCircle size={12} className="mr-1" />
                {emailError}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label
              htmlFor="password"
              className="block mb-2 text-sm font-medium text-gray-300"
            >
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock size={18} className="text-gray-400" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                autoComplete="current-password"
                aria-label="Password"
                className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent pl-10 pr-12 py-3 transition-all duration-200"
                required
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={0}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {showPassword && (
              <p className="mt-1 text-xs text-emerald-400 flex items-center">
                <Shield size={12} className="mr-1" />
                Password visible for 4 seconds
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-800 flex items-center justify-center"
            aria-busy={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                <span>Logging in...</span>
              </>
            ) : (
              <span>Login</span>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-gray-800 text-gray-400">Or continue with</span>
            </div>
          </div>
        </div>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleSignIn}
          disabled={googleSignInActive || loading}
          className="mt-6 w-full bg-white text-gray-900 p-3 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-gray-800 flex items-center justify-center font-medium"
          aria-label="Sign in with Google"
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>Sign in with Google</span>
        </button>

        {/* Forgot Password Link */}
        <div className="text-center mt-6">
          <Link
            to="/password-reset"
            className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors duration-200 underline-offset-2 hover:underline"
          >
            Forgot Password?
          </Link>
        </div>

        {/* Security Badge */}
        <div className="mt-8 pt-6 border-t border-gray-700">
          <div className="flex items-center justify-center text-xs text-gray-400">
            <Shield size={14} className="mr-2 text-green-400" />
            <span>Secured with Firebase Authentication & HTTPS</span>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;